import { makeAction, makeReducer } from '@finn-no/redux-actions';

import { serverDate } from 'shared/locale/datetime.js';

const actions = {};

actions.requestReceived = makeAction('SEARCH_FORM/REQUEST_RECEIVED', (state, { payload }) => ({
    ...state,
    isOneway: payload.tripType === 'oneway',
    isRoundtrip: payload.tripType === 'roundtrip',
    isMulticity: payload.tripType === 'openjaw',
    tripType: payload.tripType,
    departureDate: serverDate(payload.requestedDepartureDate),
    returnDate: payload.requestedReturnDate ? serverDate(payload.requestedReturnDate) : null,
    origin: payload.requestedOrigin,
    originCity: payload.requestedOriginCity,
    originCountry: payload.requestedOriginCountry,
    originText: payload.requestedOriginName,
    destination: payload.requestedDestination,
    destinationCity: payload.requestedDestinationCity,
    destinationCountry: payload.requestedDestinationCountry,
    destinationText: payload.requestedDestinationName,
    origin2: payload.requestedOrigin2,
    originCity2: payload.requestedOriginCity2,
    originCountry2: payload.requestedOriginCountry2,
    originText2: payload.requestedOriginName2,
    destination2: payload.requestedDestination2,
    destinationCity2: payload.requestedDestinationCity2,
    destinationCountry2: payload.requestedDestinationCountry2,
    destinationText2: payload.requestedDestinationName2,
    adults: +payload.numberOfAdults,
    children: payload.childAges ? payload.childAges.map(child => +child) : [],
    cabinType: payload.cabinType,
}));

actions.setData = makeAction('SEARCH_FORM/SET_DATA', (state, { payload }) => ({ ...state, payload }));

actions.setPristine = makeAction('SEARCH_FORM/SET_PRISTINE', 'pristine');

actions.setTripType = makeAction('SEARCH_FORM/SET_TRIP_TYPE', (state, { payload }) => ({
    ...state,
    pristine: true,
    tripType: payload,
    isOneway: payload === 'oneway',
    isRoundtrip: payload === 'roundtrip',
    isMulticity: payload === 'openjaw',
}));

actions.setDepartureDate = makeAction('SEARCH_FORM/SET_DEPARTURE_DATE', 'departureDate');

actions.setReturnDate = makeAction('SEARCH_FORM/SET_RETURN_DATE', 'returnDate');

actions.addAdult = makeAction('SEARCH_FORM/ADD_ADULT', state =>
    state.adults < 9 ? { ...state, adults: state.adults + 1, showPassengerWarning: state.adults + 1 + state.children.length > 9 } : state
);

actions.removeAdult = makeAction('SEARCH_FORM/REMOVE_ADULT', state =>
    state.adults > 1 ? { ...state, adults: state.adults - 1, showPassengerWarning: state.adults - 1 + state.children.length > 9 } : state
);

actions.setAdults = makeAction('SEARCH_FORM/SET_ADULTS', (state, { payload }) =>
    !isNaN(payload) && payload <= 9 && payload >= 1
        ? { ...state, adults: +payload, showPassengerWarning: +payload + state.children.length > 9 }
        : state
);

actions.addChild = makeAction('SEARCH_FORM/ADD_CHILD', state => {
    const children = state.children.slice();
    children.push(8);

    return state.children.length < 9 ? { ...state, children, showPassengerWarning: state.adults + children.length > 9 } : state;
});

actions.removeChild = makeAction('SEARCH_FORM/REMOVE_CHILD', state => {
    const children = state.children.slice();
    children.pop();

    return state.children.length > 0 ? { ...state, children, showPassengerWarning: state.adults + children.length > 9 } : state;
});

actions.setChildren = makeAction('SEARCH_FORM/SET_CHILDREN', (state, { payload }) => {
    const children = [];
    if (payload > 9) {
        payload = 9;
    }
    for (let i = 0; i < payload; i++) {
        children.push(state.children[i] ? state.children[i] : 8);
    }
    return { ...state, children, showPassengerWarning: state.adults + children.length > 9 };
});

actions.increaseChildAge = makeAction('SEARCH_FORM/INC_CHILD_AGE', (state, { payload }) => {
    const children = state.children.slice();
    ++children[payload];

    return state.children[payload] < 17 ? { ...state, children } : state;
});

actions.decreaseChildAge = makeAction('SEARCH_FORM/DEC_CHILD_AGE', (state, { payload }) => {
    const children = state.children.slice();
    --children[payload];

    return state.children[payload] > 0 ? { ...state, children } : state;
});

actions.setChildAge = makeAction('SEARCH_FORM/SET_CHILD_AGE', (state, { payload }) => {
    const children = state.children.slice();
    if (!isNaN(payload.value) && payload.value >= 0 && payload.value <= 17) {
        children[payload.index] = +payload.value;
    }
    return { ...state, children };
});

actions.setCabinType = makeAction('SEARCH_FORM/SET_CABIN_TYPE', 'cabinType');

actions.setAutocomplete = makeAction('SEARCH_FORM/SET_AUTOCOMPLETE', (state, { payload }) => ({
    ...state,
    [`${payload.field}`]: payload.value && payload.value.id ? payload.value.id : '',
    [`${payload.field}Text`]: payload.value && payload.value.text ? payload.value.text : '',
    [`${payload.field}City`]: payload.value && payload.value.data ? payload.value.data.cityName : '',
    [`${payload.field}Country`]: payload.value && payload.value.data ? payload.value.data.countryName : '',
}));

actions.setAutocompleteMultiCity = makeAction('SEARCH_FORM/SET_AUTOCOMPLETE_MC', (state, { payload }) => ({
    ...state,
    [`${payload.field}2`]: payload.value && payload.value.id ? payload.value.id : '',
    [`${payload.field}Text2`]: payload.value && payload.value.text ? payload.value.text : '',
    [`${payload.field}City2`]: payload.value && payload.value.data ? payload.value.data.cityName : '',
    [`${payload.field}Country2`]: payload.value && payload.value.data ? payload.value.data.countryName : '',
}));

actions.setAutocompleteText = makeAction('SEARCH_FORM/SET_AUTOCOMPLETE_TEXT', (state, { payload }) => ({
    ...state,
    [`${payload.field}`]: '',
    [`${payload.field}Text`]: payload.value,
    [`${payload.field}City`]: '',
    [`${payload.field}Country`]: '',
}));

actions.setAutocompleteTextMultiCity = makeAction('SEARCH_FORM/SET_AUTOCOMPLETE_TEXT_MC', (state, { payload }) => ({
    ...state,
    [`${payload.field}2`]: '',
    [`${payload.field}Text2`]: payload.value,
    [`${payload.field}City2`]: '',
    [`${payload.field}Country2`]: '',
}));

// The default export contains all actions
export default actions;

// The exported reducer handles actions created with plain action creators
export const reducer = makeReducer(actions);
